import '.b4{color:inherit;}.ssr-plugin'
import '.b5{text-decoration:none;}.ssr-plugin'
import { styled, type Style } from 'inlines'
import {
  type AnchorHTMLAttributes,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
} from 'react'
import { useLocation } from 'wouter'

const EXTERNAL_URL_PREFIXES = ['http://', 'https://', 'mailto:', 'tel:', '#']

const isExternalUrl = (url: string) => {
  return EXTERNAL_URL_PREFIXES.some((prefix) => url.startsWith(prefix))
}

type LinkAnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  children: ReactNode
  to: string
  disabled?: boolean
  style?: Style
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

const shouldHandleInternally = (
  event: MouseEvent<HTMLAnchorElement>,
  target?: string,
) => {
  if (event.defaultPrevented) {
    return false
  }

  if (event.button !== 0) {
    return false
  }

  if (target && target !== '_self') {
    return false
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false
  }

  return true
}

const isSameDocumentHashUrl = (url: string) => {
  if (typeof window === 'undefined' || !url.includes('#')) {
    return false
  }

  if (url.trim().toLowerCase().startsWith('//')) {
    return false
  }

  let resolvedUrl: URL
  try {
    resolvedUrl = new URL(url, window.location.href)
  } catch {
    return false
  }

  if (
    resolvedUrl.protocol === 'javascript:' ||
    resolvedUrl.protocol === 'data:'
  ) {
    return false
  }

  return (
    resolvedUrl.origin === window.location.origin &&
    resolvedUrl.pathname === window.location.pathname &&
    resolvedUrl.search === window.location.search
  )
}

export const LinkAnchor = ({
  children,
  to,
  onClick,
  style,
  target,
  rel,
  disabled,
  ...props
}: LinkAnchorProps) => {
  const [, navigate] = useLocation()
  const isExternal = isExternalUrl(to)
  const isHttpUrl = to.startsWith('http://') || to.startsWith('https://')
  const resolvedTarget = target ?? (isHttpUrl ? '_blank' : undefined)
  const resolvedRel =
    rel ?? (resolvedTarget === '_blank' ? 'noopener noreferrer' : undefined)

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    onClick?.(event)

    if (event.defaultPrevented) {
      return
    }

    if (disabled) {
      event.preventDefault()
      return
    }

    if (isExternal || !shouldHandleInternally(event, resolvedTarget)) {
      return
    }

    if (isSameDocumentHashUrl(to)) {
      return
    }

    event.preventDefault()
    navigate(to)
  }

  return (
    <styled.a className="b4 b5"
      {...props}
      aria-disabled={disabled || undefined}
      href={to}
      onClick={handleClick}
      rel={resolvedRel}
      target={resolvedTarget}
      style={{
        ...(disabled
          ? {
              cursor: 'not-allowed',
              pointerEvents: 'none',
            }
          : {}),
        ...style,
      }}
    >
      {children}
    </styled.a>
  )
}
